import {
  AccredibleSelectInputComponentModule,
  AccredibleSelectInputOption,
} from '@accredible-frontend-v2/forms/select-input';
import { AccredibleTextInputComponentModule } from '@accredible-frontend-v2/forms/text-input';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { debounceTime, filter, tap, withLatestFrom } from 'rxjs/operators';
import { SpotlightSearchByOptions } from '../../../../core/models/spotlight-profiles.model';
import { FilterService } from '../../services/filter.service';
import {
  SpotlightProfilesState,
  SpotlightProfilesStateAction,
} from '../../store/spotlight-profiles/spotlight-profiles.reducer';
import {
  selectSpotlightProfilesFilters,
  selectSpotlightProfilesStateAction,
} from '../../store/spotlight-profiles/spotlight-profiles.selectors';

enum SearchFieldTranslations {
  PROFILE_NAME = 'profile-list-search.profile_name',
  RECIPIENT_NAME = 'profile-list-search.recipient_name',
  EMAIL_ADDRESS = 'profile-list-search.email_address',
  RECIPIENT_ID = 'profile-list-search.recipient_id',
  CREDENTIAL_ID = 'profile-list-search.credential_id',
}

enum SearchFieldPlaceholders {
  DEFAULT = 'profile-list-search.placeholder_search_by',
  EMAIL_ADDRESS = 'profile-list-search.placeholder_email',
  ID = 'profile-list-search.placeholder_id',
}

@Component({
  selector: 'sl-profile-list-search',
  templateUrl: './profile-list-search.component.html',
  styleUrls: ['./profile-list-search.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AccredibleTextInputComponentModule,
    AccredibleSelectInputComponentModule,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileListSearchComponent implements OnInit {
  private readonly _spotlightProfileStore = inject(Store<SpotlightProfilesState>);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _filter = inject(FilterService);
  private readonly _destroyRef = inject(DestroyRef);

  availableSearchByFilters = input.required<string[]>();

  searchByOptions = computed(() =>
    this._getAvailableSearchByOptions(this.availableSearchByFilters()),
  );

  searchFormControl = new FormControl('');
  currentPlaceholder = this._language.translate(SearchFieldPlaceholders.DEFAULT);
  searchByFormControl = new FormControl(SpotlightSearchByOptions.PROFILE_NAME);
  defaultSearchByOption = SpotlightSearchByOptions.PROFILE_NAME;

  ngOnInit(): void {
    this._listenToSearchCriteriaChange();
    this._listenToSearchByCriteriaChange();
    this._listenToFiltersUpdated();
    this._listenToFiltersReset();
  }

  onChangeSearchCriteria(searchTerm: string): void {
    // If there is no search term, we remove the searchTerm query param
    this._filter.updateRouterQueryParams({
      search_term: searchTerm?.length > 0 ? searchTerm : null,
      search_by:
        this.searchByFormControl.value !== SpotlightSearchByOptions.PROFILE_NAME
          ? this.searchByFormControl.value
          : null,
    });
  }

  private _resetInputs(): void {
    this.searchFormControl.reset('', { emitEvent: false });
    this.searchByFormControl.reset(this.defaultSearchByOption);
  }

  private _onChangeSearchByCriteria(searchBy: SpotlightSearchByOptions): void {
    this._updateSearchPlaceholder(searchBy);
    // If searchBy is set to name we remove the searchBy query param
    this._filter.updateRouterQueryParams({
      search_by: searchBy === SpotlightSearchByOptions.PROFILE_NAME ? null : searchBy,
    });
  }

  private _listenToSearchCriteriaChange(): void {
    this.searchFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        debounceTime(500),
        withLatestFrom(this._spotlightProfileStore.select(selectSpotlightProfilesFilters)),
        filter(([searchTerm, currentFilters]) => searchTerm !== currentFilters.search_term),
      )
      .subscribe(([searchTerm]) => {
        this.onChangeSearchCriteria(searchTerm);
      });
  }

  private _listenToSearchByCriteriaChange(): void {
    this.searchByFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((searchBy: SpotlightSearchByOptions) => this._updateSearchPlaceholder(searchBy)),
        withLatestFrom(this._spotlightProfileStore.select(selectSpotlightProfilesFilters)),
        filter(
          ([searchBy, currentFilters]) =>
            searchBy !== currentFilters?.search_by && !!currentFilters?.search_term,
        ),
      )
      .subscribe(([searchBy]) => {
        this._onChangeSearchByCriteria(searchBy);
      });
  }

  private _listenToFiltersUpdated(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesFilters)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((filters) => !!filters),
      )
      .subscribe((filters) => {
        this.searchFormControl.patchValue(filters.search_term ?? null, { emitEvent: false });
        this.searchByFormControl.patchValue(filters.search_by ?? this.defaultSearchByOption, {
          emitEvent: false,
        });
      });
  }

  private _listenToFiltersReset(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesStateAction)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((action) => action === SpotlightProfilesStateAction.FILTERS_RESET),
      )
      .subscribe(() => {
        this._resetInputs();
      });
  }

  private _updateSearchPlaceholder(currentOption: SpotlightSearchByOptions): void {
    const placeholderMapping: Record<string, SearchFieldPlaceholders> = {
      [SpotlightSearchByOptions.EMAIL_ADDRESS]: SearchFieldPlaceholders.EMAIL_ADDRESS,
      [SpotlightSearchByOptions.CREDENTIAL_ID]: SearchFieldPlaceholders.ID,
      [SpotlightSearchByOptions.RECIPIENT_ID]: SearchFieldPlaceholders.ID,
    };

    const selectedPlaceholder =
      placeholderMapping[currentOption] || SearchFieldPlaceholders.DEFAULT;

    this.currentPlaceholder = this._language.translate(selectedPlaceholder);
  }

  private _getAvailableSearchByOptions(availableOptions: string[]): AccredibleSelectInputOption[] {
    // If the only available option is the name, then we don't want to show the dropdown
    if (availableOptions.length === 1 && availableOptions[0] === 'name') {
      return [];
    }
    const searchByConfigurations = [
      {
        translationKey: SearchFieldTranslations.PROFILE_NAME,
        value: SpotlightSearchByOptions.PROFILE_NAME,
        key: 'name',
      },
      {
        translationKey: SearchFieldTranslations.RECIPIENT_NAME,
        value: SpotlightSearchByOptions.RECIPIENT_NAME,
        key: 'recipient_name',
      },
      {
        translationKey: SearchFieldTranslations.EMAIL_ADDRESS,
        value: SpotlightSearchByOptions.EMAIL_ADDRESS,
        key: 'recipient_email',
      },
      {
        translationKey: SearchFieldTranslations.RECIPIENT_ID,
        value: SpotlightSearchByOptions.RECIPIENT_ID,
        key: 'recipient_id',
      },
      {
        translationKey: SearchFieldTranslations.CREDENTIAL_ID,
        value: SpotlightSearchByOptions.CREDENTIAL_ID,
        key: 'credential_uuid',
      },
    ];

    return searchByConfigurations
      .filter(({ key }) => availableOptions.includes(key))
      .map(({ translationKey, value }) => ({
        name: this._language.translate(translationKey),
        value,
      }));
  }
}
